import React, { useState, useMemo } from "react";
import {
  Box,
  Typography,
  Button,
  Grid,
  Chip,
  IconButton,
  CircularProgress,
  Card,
  CardContent,
  Divider,
  Avatar,
  Badge,
  Fade,
  Zoom,
  ButtonBase,
  Stack,
} from "@mui/material";
import {
  ChevronLeft,
  ChevronRight,
  AccessTime,
  Person,
  Schedule,
  CheckCircle,
  TodayOutlined,
  EventAvailable,
} from "@mui/icons-material";
import { dateUtils } from "../utils/dateUtils";

function CalendarView({ availableSlots, lessonTypes, selectedSlot, onSlotSelect, loading }) {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [viewMode, setViewMode] = useState('calendar'); // 'calendar' or 'list'
  const [selectedDate, setSelectedDate] = useState(null);

  // Group slots by date
  const slotsByDate = useMemo(() => {
    return dateUtils.groupSlotsByDate(availableSlots);
  }, [availableSlots]);

  // Get calendar weeks for current month
  const calendarWeeks = useMemo(() => {
    return dateUtils.getCalendarWeeks(currentDate);
  }, [currentDate]);

  const navigateMonth = (direction) => {
    const newDate = new Date(currentDate);
    newDate.setMonth(currentDate.getMonth() + direction);
    setCurrentDate(newDate);
  };

  const navigateToToday = () => {
    setCurrentDate(new Date());
  };

  const getDaySlotsCount = (date) => {
    const dateKey = dateUtils.toISODateString(date);
    return slotsByDate[dateKey]?.length || 0;
  };


  const getLessonTypeById = (id) => {
    let lessonType = lessonTypes.find((type) => type.id === id);

    // If not found, try matching by name (fallback for hardcoded IDs)
    if (!lessonType) {
      const nameMap = {
        'coaching-individuale': 'Coaching Individuale',
        'coaching-gruppo': 'Coaching di Gruppo',
        'consulenza-breve': 'Consulenza Breve',
        'workshop-tematico': 'Workshop Tematico'
      };

      const expectedName = nameMap[id];
      if (expectedName) {
        lessonType = lessonTypes.find((type) => type.name === expectedName);
      }
    }

    return lessonType;
  };

  const isSlotSelected = (slot) => {
    return (
      selectedSlot &&
      dateUtils.toISODateString(selectedSlot.date) === dateUtils.toISODateString(slot.date) &&
      selectedSlot.startTime === slot.startTime &&
      selectedSlot.lessonTypeId === slot.lessonTypeId
    );
  };

  const handleSlotClick = (slot) => {
    // Only allow selection if slot is bookable
    if (slot.isBookable !== false) {
      onSlotSelect(slot);
      // Scroll alla sezione di conferma
      setTimeout(() => {
        const element = document.getElementById('selected-slot-summary');
        if (element) {
          element.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      }, 300); // Delay per permettere al componente di renderizzare
    }
  };

  if (loading) {
    return (
      <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress size={60} thickness={4} sx={{ mb: 2 }} />
        <Typography variant="h6" color="textSecondary">
          Caricamento slot disponibili...
        </Typography>
        <Typography variant="body2" color="textSecondary" mt={1}>
          Stiamo preparando i tuoi orari disponibili
        </Typography>
      </Box>
    );
  }

  if (availableSlots.length === 0) {
    return (
      <Card elevation={0} sx={{ textAlign: 'center', py: 6, backgroundColor: 'grey.50' }}>
        <CardContent>
          <EventAvailable sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h5" gutterBottom>
            Nessuno slot disponibile
          </Typography>
          <Typography variant="body1" color="textSecondary" paragraph>
            Al momento non ci sono orari disponibili per le prenotazioni.
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Riprova più tardi o contattaci direttamente per maggiori informazioni.
          </Typography>
          <Button
            variant="contained"
            sx={{ mt: 2 }}
            onClick={() => window.location.href = 'mailto:info@ilariastumpo.it'}
          >
            Contattaci
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <Box>
      {/* Enhanced Header */}
      <Card elevation={2} sx={{ mb: 4, background: '#145444', color: 'white' }}>
        <CardContent sx={{ py: { xs: 1.5, sm: 3 } }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
            {/* Month Navigation */}
            <Box display="flex" alignItems="center" gap={1}>
              <IconButton
                onClick={() => navigateMonth(-1)}
                sx={{ color: 'white', backgroundColor: 'rgba(255,255,255,0.1)', '&:hover': { backgroundColor: 'rgba(255,255,255,0.2)' } }}
              >
                <ChevronLeft />
              </IconButton>
              <Typography variant="h4" component="h2" sx={{ minWidth: { xs: "180px", sm: "250px" }, textAlign: "center", fontWeight: 'bold', fontSize: { xs: '1.5rem', sm: '2.125rem' }, textTransform: 'uppercase' }}>
                {dateUtils.getMonthName(currentDate)}
              </Typography>
              <IconButton
                onClick={() => navigateMonth(1)}
                sx={{ color: 'white', backgroundColor: 'rgba(255,255,255,0.1)', '&:hover': { backgroundColor: 'rgba(255,255,255,0.2)' } }}
              >
                <ChevronRight />
              </IconButton>
            </Box>

            {/* Action Buttons */}
            <Stack direction="row" spacing={1}>
              <Button
                variant="outlined"
                size="small"
                startIcon={<TodayOutlined />}
                onClick={navigateToToday}
                sx={{
                  color: 'white',
                  borderColor: 'rgba(255,255,255,0.5)',
                  '&:hover': { borderColor: 'white', backgroundColor: 'rgba(255,255,255,0.1)' }
                }}
              >
                Oggi
              </Button>
              <Button
                variant={viewMode === 'list' ? 'contained' : 'outlined'}
                size="small"
                onClick={() => setViewMode(viewMode === 'calendar' ? 'list' : 'calendar')}
                sx={{
                  color: 'white',
                  borderColor: 'rgba(255,255,255,0.5)',
                  backgroundColor: viewMode === 'list' ? 'rgba(255,255,255,0.2)' : 'transparent',
                  '&:hover': { borderColor: 'white', backgroundColor: 'rgba(255,255,255,0.1)' }
                }}
              >
                {viewMode === 'calendar' ? 'Lista' : 'Calendario'}
              </Button>
            </Stack>
          </Box>

          {/* Quick Stats */}
          <Box mt={2} display="flex" gap={3} flexWrap="wrap">
            <Box display="flex" alignItems="center" gap={1}>
              <EventAvailable fontSize="small" />
              <Typography variant="body2">
                {availableSlots.length} slot disponibili questo mese
              </Typography>
            </Box>
            <Box display="flex" alignItems="center" gap={1}>
              <Schedule fontSize="small" />
              <Typography variant="body2">
                {lessonTypes.length} tipologie di lezione
              </Typography>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Modern Calendar Grid */}
      {viewMode === 'calendar' && (
        <Fade in={viewMode === 'calendar'}>
          <Card elevation={1} sx={{ mb: 4 }}>
            <CardContent sx={{ p: 3 }}>
              <Grid container spacing={1}>
                {/* Elegant Day Headers */}
                {["Lun", "Mar", "Mer", "Gio", "Ven", "Sab", "Dom"].map((day) => (
                  <Grid item xs={12/7} key={day}>
                    <Typography
                      variant="subtitle2"
                      textAlign="center"
                      fontWeight="600"
                      color="primary.main"
                      sx={{ py: 2, borderBottom: '2px solid', borderColor: 'primary.light' }}
                    >
                      {day}
                    </Typography>
                  </Grid>
                ))}

                {/* Enhanced Calendar Days */}
                {calendarWeeks.map((week, weekIndex) =>
                  week.map((day, dayIndex) => {
                    const slotsCount = getDaySlotsCount(day.date);
                    const isPast = dateUtils.isPastDate(day.date);
                    const isToday = dateUtils.isToday(day.date);
                    const dateKey = dateUtils.toISODateString(day.date);
                    const isSelected = selectedDate === dateKey;

                    return (
                      <Grid item xs={12/7} key={`${weekIndex}-${dayIndex}`}>
                        <ButtonBase
                          onClick={() => {
                            if (slotsCount > 0 && !isPast && day.isCurrentMonth) {
                              setSelectedDate(isSelected ? null : dateKey);
                              const element = document.getElementById('available-slots-section');
                              if (element) {
                                element.scrollIntoView({ behavior: "smooth", block: "start" });
                              }
                            }
                          }}
                          sx={{
                            width: '100%',
                            height: '80px',
                            borderRadius: 2,
                            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                          }}
                          disabled={!slotsCount || isPast || !day.isCurrentMonth}
                        >
                          <Box
                            sx={{
                              width: '100%',
                              height: '100%',
                              p: 1,
                              position: 'relative',
                              borderRadius: 2,
                              opacity: day.isCurrentMonth ? 1 : 0.3,
                              backgroundColor: isSelected
                                ? 'primary.main'
                                : isToday
                                ? 'warning.light'
                                : isPast
                                ? 'grey.100'
                                : slotsCount > 0
                                ? 'success.light'
                                : 'background.paper',
                              border: isToday ? '3px solid' : '2px solid',
                              borderColor: isSelected
                                ? 'primary.main'
                                : isToday
                                ? 'warning.main'
                                : slotsCount > 0 && !isPast
                                ? 'success.main'
                                : 'grey.300',
                              color: isSelected || (isToday && slotsCount > 0) ? 'white' : 'inherit',
                              '&:hover': {
                                transform: slotsCount > 0 && !isPast && day.isCurrentMonth ? 'translateY(-2px)' : 'none',
                                boxShadow: slotsCount > 0 && !isPast && day.isCurrentMonth ? 4 : 0,
                                backgroundColor: isSelected
                                  ? 'primary.dark'
                                  : slotsCount > 0 && !isPast && day.isCurrentMonth
                                  ? 'success.main'
                                  : undefined,
                                color: slotsCount > 0 && !isPast && day.isCurrentMonth ? 'white' : undefined,
                              },
                            }}
                          >
                            <Typography
                              variant="h6"
                              fontWeight={isToday ? "bold" : "normal"}
                              sx={{ mb: 0.5, fontSize: 'calc(1.25rem - 3px)' }}
                            >
                              {day.dayOfMonth}
                            </Typography>

                            {slotsCount > 0 && day.isCurrentMonth && (
                              <Badge
                                badgeContent={slotsCount}
                                color={isPast ? "default" : "secondary"}
                                sx={{
                                  position: 'absolute',
                                  top: 4,
                                  right: 4,
                                  '& .MuiBadge-badge': {
                                    fontSize: '0.6rem',
                                    minWidth: '16px',
                                    height: '16px',
                                  }
                                }}
                              />
                            )}

                            {isToday && (
                              <Chip
                                label="Oggi"
                                size="small"
                                sx={{
                                  position: 'absolute',
                                  bottom: 2,
                                  left: 2,
                                  right: 2,
                                  fontSize: '0.6rem',
                                  height: '16px',
                                }}
                              />
                            )}
                          </Box>
                        </ButtonBase>
                      </Grid>
                    );
                  })
                )}
              </Grid>
            </CardContent>
          </Card>
        </Fade>
      )}

      {/* Premium Slots Display */}
      <Box id="available-slots-section" sx={{ scrollMarginTop: '100px' }}>
        <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold', mb: 3 }}>
          {viewMode === 'list' ? '📅 Tutti gli Slot Disponibili' : '🕐 Orari Disponibili'}
        </Typography>

        {/* Filter by selected date in calendar mode */}
        {Object.entries(slotsByDate)
          .filter(([dateKey]) => {
            const date = dateUtils.fromISODateString(dateKey);
            const matchesMonth = date.getMonth() === currentDate.getMonth() && date.getFullYear() === currentDate.getFullYear();
            const isNotPast = !dateUtils.isPastDate(date);
            const matchesSelectedDate = viewMode === 'list' || !selectedDate || selectedDate === dateKey;
            return isNotPast && matchesMonth && matchesSelectedDate;
          })
          .sort(([a], [b]) => a.localeCompare(b))
          .map(([dateKey, daySlots]) => {
            const date = dateUtils.fromISODateString(dateKey);
            const sortedSlots = dateUtils.sortSlotsByTime(daySlots);

            return (
              <Fade in={true} key={dateKey}>
                <Card
                  elevation={selectedDate === dateKey ? 4 : 2}
                  id={`slots-${dateKey}`}
                  sx={{
                    mb: 4,
                    border: selectedDate === dateKey ? '2px solid' : '1px solid',
                    borderColor: selectedDate === dateKey ? 'primary.main' : 'grey.300',
                    overflow: 'visible',
                    scrollMarginTop: '100px'
                  }}
                >
                  {/* Date Header */}
                  <Box
                    sx={{
                      background: selectedDate === dateKey
                        ? '#145444'
                        : '#145444',
                      color: 'white',
                      p: 2,
                      position: 'relative'
                    }}
                  >
                    <Typography variant="h6" fontWeight="bold">
                      {dateUtils.formatDate(date, "EEEE, dd MMMM yyyy")}
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.9 }}>
                      {sortedSlots.length} slot disponibili
                    </Typography>
                    {dateUtils.isToday(date) && (
                      <Chip
                        label="OGGI"
                        size="small"
                        sx={{
                          position: 'absolute',
                          top: 8,
                          right: 8,
                          backgroundColor: 'rgba(255,255,255,0.2)',
                          color: 'white',
                          fontWeight: 'bold'
                        }}
                      />
                    )}
                  </Box>

                  <CardContent sx={{ p: 3 }}>
                    <Grid container spacing={3}>
                      {sortedSlots.map((slot, index) => {
                        const lessonType = getLessonTypeById(slot.lessonTypeId);
                        const isSelected = isSlotSelected(slot);
                        const isNotBookable = slot.isBookable === false;

                        return (
                          <Grid item xs={12} sm={6} lg={4} key={`${dateKey}-${index}`}>
                            <Zoom in={true} style={{ transitionDelay: `${index * 100}ms` }}>
                              <Card
                                elevation={isSelected ? 8 : 3}
                                sx={{
                                  cursor: isNotBookable ? "not-allowed" : "pointer",
                                  position: 'relative',
                                  height: '100%',
                                  background: '#efeae6',
                                  border: isSelected ? '3px solid' : '2px solid',
                                  borderColor: isSelected ? 'primary.main' : lessonType?.color || 'grey.300',
                                  transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                                  '&:hover': {
                                    transform: isNotBookable ? 'none' : 'translateY(-8px)',
                                    boxShadow: isNotBookable ? 3 : 8,
                                    borderColor: lessonType?.color || 'primary.main',
                                  },
                                }}
                                onClick={() => handleSlotClick(slot)}
                              >
                                {/* Selection indicator */}
                                {isSelected && (
                                  <Box
                                    sx={{
                                      position: 'absolute',
                                      top: -8,
                                      right: -8,
                                      zIndex: 2,
                                    }}
                                  >
                                    <Avatar
                                      sx={{
                                        backgroundColor: 'success.main',
                                        width: 24,
                                        height: 24,
                                      }}
                                    >
                                      <CheckCircle fontSize="small" />
                                    </Avatar>
                                  </Box>
                                )}

                                <CardContent sx={{ p: 2.5 }}>
                                  {/* Time Display */}
                                  <Box display="flex" alignItems="center" gap={1} mb={2}>
                                    <Avatar
                                      sx={{
                                        backgroundColor: lessonType?.color || 'primary.main',
                                        width: 32,
                                        height: 32,
                                      }}
                                    >
                                      <AccessTime fontSize="small" />
                                    </Avatar>
                                    <Box>
                                      <Typography variant="h6" fontWeight="bold">
                                        {slot.startTime} - {slot.endTime}
                                      </Typography>
                                      <Typography variant="caption" sx={{ opacity: 0.8 }}>
                                        {dateUtils.calculateDuration(slot.startTime, slot.endTime)}
                                      </Typography>
                                    </Box>
                                  </Box>

                                  {/* Lesson Type */}
                                  <Typography
                                    variant="subtitle1"
                                    fontWeight="600"
                                    gutterBottom
                                  >
                                    {lessonType?.name || "Tipo sconosciuto"}
                                  </Typography>

                                  <Divider sx={{ my: 2, opacity: 0.3 }} />

                                  {/* Details */}
                                  <Stack spacing={1}>
                                    <Box display="flex" alignItems="center" gap={1}>
                                      <Person fontSize="small" sx={{ opacity: 0.7 }} />
                                      <Typography variant="body2">
                                        {isNotBookable
                                          ? "Non prenotabile"
                                          : slot.availableSpots > 0
                                            ? `${slot.availableSpots} ${slot.availableSpots === 1 ? "posto disponibile" : "posti disponibili"}`
                                            : "Completo"
                                        }
                                      </Typography>
                                    </Box>

                                    {lessonType?.duration && (
                                      <Box display="flex" alignItems="center" gap={1}>
                                        <Schedule fontSize="small" sx={{ opacity: 0.7 }} />
                                        <Typography variant="body2">
                                          {lessonType.duration} minuti
                                        </Typography>
                                      </Box>
                                    )}
                                  </Stack>

                                  {/* Action Button */}
                                  <Button
                                    variant={isSelected ? "outlined" : "contained"}
                                    fullWidth
                                    disabled={isNotBookable}
                                    sx={{
                                      mt: 2,
                                      backgroundColor: isNotBookable ? 'grey.400' : (lessonType?.color || 'primary.main'),
                                      color: 'white',
                                      '&:hover': {
                                        backgroundColor: isNotBookable ? 'grey.400' : ((lessonType?.color + 'DD') || 'primary.dark'),
                                      }
                                    }}
                                  >
                                    {isNotBookable ? 'Non disponibile' : (isSelected ? '✓ Selezionato' : 'Seleziona')}
                                  </Button>
                                </CardContent>
                              </Card>
                            </Zoom>
                          </Grid>
                        );
                      })}
                    </Grid>
                  </CardContent>
                </Card>
              </Fade>
            );
          })}
      </Box>

      {/* Selection Summary */}
      {selectedSlot && (
        <Zoom in={true}>
          <Card
            id="selected-slot-summary"
            elevation={6}
            sx={{
              mt: 4,
              background: '#145444',
              color: 'white',
              border: '3px solid',
              borderColor: 'success.main',
              scrollMarginTop: '100px',
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Box display="flex" alignItems="center" gap={2}>
                <Avatar sx={{ backgroundColor: 'rgba(255,255,255,0.2)' }}>
                  <CheckCircle />
                </Avatar>
                <Box flex={1}>
                  <Typography variant="h6" fontWeight="bold" gutterBottom>
                    🎉 Perfetto! Slot selezionato
                  </Typography>
                  <Typography variant="body1">
                    <strong>{dateUtils.formatDate(selectedSlot.date, "EEEE, dd MMMM yyyy")}</strong>
                    {" alle "}
                    <strong>{selectedSlot.startTime}</strong>
                    {" - "}
                    <strong>{getLessonTypeById(selectedSlot.lessonTypeId)?.name}</strong>
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Zoom>
      )}
    </Box>
  );
}

export default CalendarView;